import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { defer } from 'rxjs';
import { finalize, map, switchMap, tap } from 'rxjs/operators';
import { Router } from '@angular/router';

import { SocialAuthService } from '@abacritt/angularx-social-login';

import { LoginRequest, LoginResponse, RegisterRequest, SwitchOrgResponse } from '../dtos/auth-dto';
import { User, UserOrganization } from '../domain/user-model';
import { environment } from 'src/environments/environment';
import { HttpDTO, PaginatedHttpDTO } from '../dtos/http-dto';
import { PermissionService } from './permission-service';
import { PostHogService } from './posthog.service';
import {
  clearLastOrganization,
  currentOrganizationOf,
  storeLastOrganization,
} from '@/shared/utils/organization-route';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;
  private router = inject(Router);
  private socialAuthService = inject(SocialAuthService);
  private permissionService = inject(PermissionService);
  private posthogService = inject(PostHogService);
  private identifiedUserId: string | null = null;

  isAuthenticated = signal<boolean>(!!localStorage.getItem('accessToken'));
  currentUser = signal<User | null>(null);
  private readonly organizationSwitches = signal(0);
  readonly isSwitchingOrganization = computed(() => this.organizationSwitches() > 0);

  login(credentials: LoginRequest) {
    return this.http.post<HttpDTO<LoginResponse>>(`${this.apiUrl}/auth/login`, credentials).pipe(
      tap((response) => {
        localStorage.setItem('accessToken', response.data.access_token);
        storeLastOrganization(currentOrganizationOf(response.data.user));
        this.isAuthenticated.set(true);
        this.identifyUser(response.data.user);
      }),
    );
  }

  loginWithGoogle(idToken: string) {
    return this.http
      .post<HttpDTO<LoginResponse>>(`${this.apiUrl}/auth/google`, {
        id_token: idToken,
      })
      .pipe(
        tap((response) => {
          localStorage.setItem('accessToken', response.data.access_token);
          storeLastOrganization(currentOrganizationOf(response.data.user));
          this.identifyUser(response.data.user);
        }),
      );
  }

  logout() {
    this.posthogService.posthog?.reset();
    this.identifiedUserId = null;
    localStorage.removeItem('accessToken');
    clearLastOrganization();
    this.currentUser.set(null);
    this.permissionService.reset();

    this.socialAuthService.authState.subscribe((user) => {
      if (user) {
        this.socialAuthService.signOut().catch((err) => {
          console.warn('Erro ao deslogar do Google', err);
        });
      }
    });

    this.isAuthenticated.set(false);
    this.router.navigate(['/']);
  }

  register(req: RegisterRequest) {
    return this.http.post<HttpDTO<LoginResponse>>(`${this.apiUrl}/auth/register`, req).pipe(
      tap((response) => {
        localStorage.setItem('accessToken', response.data.access_token);
        this.isAuthenticated.set(true);
        this.identifyUser(response.data.user);
      }),
    );
  }

  recoverPassword(email: string) {
    return this.http.post(`${this.apiUrl}/auth/recovery`, { email });
  }

  resetPassword(code: string, email: string, password: string) {
    return this.http.post(`${this.apiUrl}/auth/reset`, { code, email, password });
  }

  getMe() {
    return this.http.get<HttpDTO<User>>(`${this.apiUrl}/users/me`).pipe(
      switchMap((res) => {
        this.currentUser.set(res.data);
        storeLastOrganization(currentOrganizationOf(res.data));
        this.identifyUser(res.data);

        return this.permissionService.syncFromUser(res.data).pipe(map(() => res));
      }),
    );
  }

  private identifyUser(user: User): void {
    if (this.identifiedUserId === user.id) {
      return;
    }

    this.posthogService.posthog?.identify(user.id, {
      email: user.email,
      name: user.name,
      role: user.role?.name,
      is_superuser: user.superuser,
      organization_id: user.current_organization_id,
    });
    this.identifiedUserId = user.id;
  }

  getOrganizations(page: number, limit: number, search: string) {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('items_per_page', limit.toString());

    if (search) {
      params = params.set('search', search);
    }

    return this.http.get<PaginatedHttpDTO<UserOrganization[]>>(`${this.apiUrl}/organizations`, {
      params,
      observe: 'response',
    });
  }

  /** Switch by organization UUID or exact slug. */
  switchOrganization(organizationRef: string) {
    return defer(() => {
      this.organizationSwitches.update((count) => count + 1);
      return this.http
        .post<
          HttpDTO<SwitchOrgResponse>
        >(`${this.apiUrl}/organizations/${encodeURIComponent(organizationRef)}/switch`, {})
        .pipe(
          tap((response) => {
            if (response.data.access_token) {
              localStorage.setItem('accessToken', response.data.access_token);
              this.permissionService.reset();
              storeLastOrganization(currentOrganizationOf(response.data.user));
            }
          }),
          switchMap(() => this.getMe()),
          finalize(() => this.organizationSwitches.update((count) => count - 1)),
        );
    });
  }
}
